import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import './Settings.css';

const Settings = () => {
  const { t } = useTranslation();

  const { data: quickInfo = null } = useQuery({
    queryKey: ['settings-quick-info'],
    queryFn: async () => {
      const [currencies, tariffTypes, tariffs] = await Promise.all([
        api.get('settings/api/currency/?is_active=true'),
        api.get('settings/api/tariff-types/'),
        api.get('settings/api/tariffs/?is_active=true'),
      ]);
      const currencyCount = Array.isArray(currencies) ? currencies.length : (currencies.count ?? currencies.results?.length ?? 0);
      const tariffTypeCount = Array.isArray(tariffTypes) ? tariffTypes.length : (tariffTypes.count ?? tariffTypes.results?.length ?? 0);
      const activeTariffCount = Array.isArray(tariffs) ? tariffs.length : (tariffs.count ?? tariffs.results?.length ?? 0);
      const allTariffs = Array.isArray(tariffs) ? tariffs : (tariffs.results || []);
      const lastUpdate = allTariffs.reduce((latest, t) => {
        const d = t.updated_at || t.created_at;
        return d && (!latest || d > latest) ? d : latest;
      }, null);
      return { currencyCount, tariffTypeCount, activeTariffCount, lastUpdate };
    },
  });

  const settingsCategories = [
    {
      title: t('settings.company.title'),
      description: t('settings.company.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M3 21l18 0" /><path d="M9 8l1 0" /><path d="M9 12l1 0" /><path d="M9 16l1 0" />
          <path d="M14 8l1 0" /><path d="M14 12l1 0" /><path d="M14 16l1 0" />
          <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" />
        </svg>
      ),
      link: '/settings/company',
      color: 'bg-azure-lt'
    },
    {
      title: t('settings.currency.title'),
      description: t('settings.currency.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M16.7 8a3 3 0 0 0 -2.7 -2h-4a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6h-4a3 3 0 0 1 -2.7 -2" />
          <path d="M12 3v3m0 12v3" />
        </svg>
      ),
      link: '/settings/currency',
      color: 'bg-blue-lt'
    },
    {
      title: t('settings.tax.title'),
      description: t('settings.tax.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M14 3v4a1 1 0 0 0 1 1h4" />
          <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
          <path d="M9 14l.01 0" /><path d="M12 14l.01 0" /><path d="M15 14l.01 0" />
          <path d="M9 17l.01 0" /><path d="M12 17l.01 0" /><path d="M15 17l.01 0" />
        </svg>
      ),
      link: '/settings/tax',
      color: 'bg-teal-lt'
    },
    {
      title: t('settings.tariffTypes.title'),
      description: t('settings.tariffTypes.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M11.5 21h-5.5a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v6" />
          <path d="M16 19h6" /><path d="M19 16v6" /><path d="M8 11h8" /><path d="M8 15h6" />
        </svg>
      ),
      link: '/settings/tariff-types',
      color: 'bg-green-lt'
    },
    {
      title: t('settings.tariffs.title'),
      description: t('settings.tariffs.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M3 9a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9z" />
          <path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2" />
          <path d="M12 12l0 .01" /><path d="M3 13h18" />
        </svg>
      ),
      link: '/settings/tariffs',
      color: 'bg-yellow-lt'
    },
    {
      title: t('settings.general.title'),
      description: t('settings.general.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
          <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
        </svg>
      ),
      link: '/settings/general',
      color: 'bg-purple-lt'
    },
    {
      title: t('settings.notifications.title'),
      description: t('settings.notifications.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M10 5a2 2 0 0 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
          <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
        </svg>
      ),
      link: '/settings/notifications',
      color: 'bg-orange-lt'
    },
    {
      title: t('settings.system.title'),
      description: t('settings.system.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M3 5a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1v-10z" />
          <path d="M7 20h10" /><path d="M9 16v4" /><path d="M15 16v4" />
        </svg>
      ),
      link: '/settings/system',
      color: 'bg-red-lt'
    },
    {
      title: t('settings.projectTypes.title'),
      description: t('settings.projectTypes.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M4 4h6v6h-6z" /><path d="M14 4h6v6h-6z" /><path d="M4 14h6v6h-6z" />
          <path d="M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
        </svg>
      ),
      link: '/settings/project-types',
      color: 'bg-indigo-lt'
    },
    {
      title: t('settings.projectStatuses.title'),
      description: t('settings.projectStatuses.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M12 7v5l3 3" />
        </svg>
      ),
      link: '/settings/project-statuses',
      color: 'bg-lime-lt'
    },
    {
      title: t('settings.projectTaskStatuses.title'),
      description: t('settings.projectTaskStatuses.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M9 11l3 3l8 -8" /><path d="M20 12v6a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h9" />
        </svg>
      ),
      link: '/settings/project-task-statuses',
      color: 'bg-teal-lt'
    },
    {
      title: t('settings.groups.title'),
      description: t('settings.groups.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
          <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
        </svg>
      ),
      link: '/settings/groups',
      color: 'bg-pink-lt'
    },
    {
      title: t('settings.importExport.title'),
      description: t('settings.importExport.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
          <path d="M7 11l5 5l5 -5" />
          <path d="M12 4l0 12" />
        </svg>
      ),
      link: '/settings/import-export',
      color: 'bg-cyan-lt'
    },
    {
      title: t('settings.assistant.title'),
      description: t('settings.assistant.subtitle'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M5 3m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" />
          <path d="M9 7l6 0" /><path d="M9 11l6 0" /><path d="M9 15l4 0" />
          <path d="M18 18l3 3" /><path d="M18 21l3 -3" />
        </svg>
      ),
      link: '/settings/assistant',
      color: 'bg-lime-lt'
    },
    {
      title: t('profile.sessions.title'),
      description: t('profile.sessions.description'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M3 5m0 1a1 1 0 0 1 1 -1h7a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-7a1 1 0 0 1 -1 -1z" />
          <path d="M7 15v2m-2 0h4" />
          <path d="M17 8h3a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-7a1 1 0 0 1 -1 -1v-1" />
        </svg>
      ),
      link: '/accounts/profile?tab=sessions',
      color: 'bg-secondary-lt'
    }
  ];

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.title')}
    >
      <Row className="row-deck row-cards">
        {settingsCategories.map((category, index) => (
          <Col lg={4} md={6} key={index}>
            <Card className="card-link card-link-pop">
              <Link to={category.link} className="text-decoration-none">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className={`avatar avatar-lg ${category.color}`}>
                      {category.icon}
                    </div>
                    <div className="ms-3 flex-fill">
                      <h3 className="card-title mb-1">{category.title}</h3>
                      <div className="text-muted small">{category.description}</div>
                    </div>
                    <div className="ms-auto">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <polyline points="9 6 15 12 9 18"></polyline>
                      </svg>
                    </div>
                  </div>
                </Card.Body>
              </Link>
            </Card>
          </Col>
        ))}
      </Row>

      <PageSection title={t('settings.quickInfo.title')}>
        <Row>
          <Col md={3}>
            <div className="mb-3">
              <div className="text-muted small">{t('settings.quickInfo.currencies')}</div>
              <div className="h3 mb-0">{quickInfo ? quickInfo.currencyCount : '-'}</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="mb-3">
              <div className="text-muted small">{t('settings.quickInfo.tariffTypes')}</div>
              <div className="h3 mb-0">{quickInfo ? quickInfo.tariffTypeCount : '-'}</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="mb-3">
              <div className="text-muted small">{t('settings.quickInfo.activeTariffs')}</div>
              <div className="h3 mb-0">{quickInfo ? quickInfo.activeTariffCount : '-'}</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="mb-3">
              <div className="text-muted small">{t('settings.quickInfo.lastUpdate')}</div>
              <div className="h3 mb-0">
                {quickInfo
                  ? (quickInfo.lastUpdate
                      ? new Date(quickInfo.lastUpdate).toLocaleDateString()
                      : '—')
                  : '-'}
              </div>
            </div>
          </Col>
        </Row>
      </PageSection>
    </PageShell>
  );
};

export default Settings;
